import React from "react";
import { Link } from "react-router-dom";
import { Star, Check, Plus, Globe2, Smartphone, MonitorSmartphone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCompare } from "@/context/CompareContext";
import OgPreviewTooltip from "@/components/OgPreviewTooltip";
import NewBadge from "@/components/NewBadge";
import UpdatedBadge from "@/components/UpdatedBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import AppLogo from "@/components/AppLogo";
import { DIRECTORY } from "@/constants/testIds";

const AppCard = ({ app, rank }) => {
  const { lang, t } = useLang();
  const { slugs, toggle } = useCompare();
  const inCompare = slugs.includes(app.slug);

  const tagline = lang === "nl" && app.tagline_nl ? app.tagline_nl : app.tagline;
  const isFree = app.pricing?.free_tier;
  const monthly = app.pricing?.monthly_usd || 0;

  return (
    <article
      data-testid={DIRECTORY.appCard(app.slug)}
      className="hac-card p-6 md:p-7 flex flex-col gap-5 relative group"
    >
      {rank !== undefined && (
        <div className="absolute -top-3 -left-3 bg-[#2563EB] text-white font-mono text-xs px-2 py-1 rounded-md">
          #{rank}
        </div>
      )}
      {app.premium && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm" data-testid={`premium-badge-${app.slug}`}>
          <Star size={10} className="fill-current" /> Premium
        </div>
      )}
      {app.recently_listed && (
        <div className="absolute top-3 left-3" data-testid={`apps-page-new-${app.slug}`}>
          <NewBadge app={app} variant="pill" />
        </div>
      )}
      {!app.recently_listed && app.recently_updated && (
        <div className="absolute top-3 left-3" data-testid={`apps-page-updated-${app.slug}`}>
          <UpdatedBadge app={app} variant="pill" />
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <OgPreviewTooltip slug={app.slug} className="flex items-center gap-3 min-w-0 flex-1">
          <Link to={`/apps/${app.slug}`} className="flex items-center gap-3 min-w-0 flex-1">
            <AppLogo app={app} className="w-12 h-12 rounded-lg" textClassName="text-lg" />
            <div className="min-w-0">
              <h3 className="font-heading text-xl text-[#0F172A] leading-tight truncate group-hover:text-[#2563EB] transition-colors flex items-center gap-1.5">
                <span className="truncate">{app.name}</span>
                <VerifiedBadge app={app} size="sm" />
              </h3>
              <div className="text-xs text-[#94A3B8] truncate">{app.country}</div>
            </div>
          </Link>
        </OgPreviewTooltip>
        <div className="flex items-center gap-1 text-sm shrink-0" data-testid={`app-card-quality-${app.slug}`}>
          {Number(app.rating) > 0 ? (
            <>
              <Star size={14} className="fill-[#2563EB] text-[#2563EB]" />
              <span className="font-mono text-[#64748B]">{app.rating.toFixed(1)}</span>
            </>
          ) : Number(app.score) > 0 ? (
            <span
              className="inline-flex items-center gap-1 bg-[#EFF6FF] text-[#2563EB] font-bold px-2 py-0.5 rounded-full text-xs"
              title="Composite score from features, AI capability and adoption (0–100). User rating appears once reviews come in."
            >
              <span className="font-mono tabular-nums">{Math.min(100, Math.round(app.score))}</span>
              <span className="text-[10px] font-bold opacity-70">/100</span>
            </span>
          ) : (
            <span className="text-xs text-[#94A3B8] italic">No reviews yet</span>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3">
        <p className="text-sm text-[#64748B] leading-relaxed line-clamp-2 flex-1">{tagline}</p>
        {app.preview_image && (
          <Link
            to={`/apps/${app.slug}`}
            className="shrink-0 block w-16 h-20 rounded-lg overflow-hidden border border-[#E2E8F0] bg-[#0F172A] shadow-sm hover:shadow-md transition-shadow"
            aria-label={`${app.name} screenshot preview`}
            data-testid={`app-card-preview-${app.slug}`}
          >
            <img
              src={app.preview_image}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover object-top"
            />
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {(app.target_audience || []).slice(0, 3).map((a) => (
          <span key={a} className="hac-chip">
            {t.audiences[a] || a}
          </span>
        ))}
      </div>

      <div className="flex items-center gap-3 text-xs text-[#64748B] mt-auto">
        {app.platforms?.ios && <Smartphone size={14} />}
        {app.platforms?.android && <Smartphone size={14} className="opacity-60" />}
        {app.platforms?.web && <MonitorSmartphone size={14} />}
        <span className="ml-auto font-mono">
          {isFree && monthly === 0 ? t.common.free : `$${monthly}${t.common.monthly}`}
        </span>
      </div>

      <div className="flex gap-2 border-t border-[#E2E8F0] pt-4">
        <Link
          to={`/apps/${app.slug}`}
          className="flex-1 text-center text-sm py-2 border border-[#E2E8F0] rounded-full hover:border-[#2563EB] hover:text-[#2563EB] hover:bg-[#EFF6FF] transition-colors"
        >
          {t.common.overview}
        </Link>
        <button
          type="button"
          onClick={() => toggle(app.slug)}
          data-testid={DIRECTORY.compareToggle(app.slug)}
          className={`px-3 py-2 text-sm rounded-full flex items-center gap-1 transition-colors ${
            inCompare
              ? "bg-[#2563EB] text-white"
              : "border border-[#E2E8F0] hover:border-[#2563EB] hover:text-[#2563EB]"
          }`}
        >
          {inCompare ? <Check size={14} /> : <Plus size={14} />}
          {inCompare ? t.common.remove : t.common.addToCompare}
        </button>
      </div>
    </article>
  );
};

export default AppCard;
